/**
 * IaEditModal — bulk-save IA attestations (handoff §3.4).
 *
 * Replace-all semantics: send the entire list every time; backend deletes
 * old rows and inserts new ones. Per IaAttestationItem the user enters:
 *   - ia_activity_code   (text)
 *   - attested_points    (typically 10 for Medium, 20 for High)
 *
 * The modal does NOT pre-populate from the server — V1 doesn't have a
 * dedicated GET for current IA attestations (the dashboard payload only
 * carries the aggregate `improvement_activities.current`). The AM enters
 * the whole list each time. This matches the "bulk-save modal UX" the
 * handoff calls out.
 */
import { useEffect, useState } from "react";
import { usePutIaAttestations } from "@/hooks/usePutIaAttestations";
import { useClinicImprovementActivities } from "@/hooks/useClinicImprovementActivities";
import type { IaAttestationItem } from "@/api/models";

export interface IaEditModalProps {
  clinicId: number;
  year: number;
  onClose: () => void;
}

interface DraftRow {
  /** Local id so list keys are stable across re-renders. */
  uiId: string;
  code: string;
  points: string; // string in the form; converted to number on save
}

function makeDraftRow(): DraftRow {
  return { uiId: crypto.randomUUID(), code: "", points: "10" };
}

export function IaEditModal({ clinicId, year, onClose }: IaEditModalProps) {
  const put = usePutIaAttestations(clinicId);
  // V1.2.0: pre-populate from the new read endpoint. Empty array (200) is
  // a valid response — modal opens with one blank row in that case. If the
  // read fails entirely, we still render with a blank row so the AM isn't
  // blocked from creating fresh attestations.
  const readQ = useClinicImprovementActivities(clinicId, { year });
  const [rows, setRows] = useState<DraftRow[]>(() => [makeDraftRow()]);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate rows from the server once the read query resolves. Only fires
  // once — subsequent edits to `rows` are local state and shouldn't be
  // overwritten by a query refetch.
  useEffect(() => {
    if (hydrated) return;
    if (!readQ.data) return;
    const serverRows = readQ.data.improvement_activities;
    if (serverRows.length === 0) {
      // No existing attestations — leave the default blank row.
      setHydrated(true);
      return;
    }
    setRows(
      serverRows.map((s) => ({
        uiId: crypto.randomUUID(),
        code: s.activity_id,
        points: s.attested_points,
      })),
    );
    setHydrated(true);
  }, [hydrated, readQ.data]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const updateRow = (uiId: string, patch: Partial<DraftRow>) => {
    setRows((prev) => prev.map((r) => (r.uiId === uiId ? { ...r, ...patch } : r)));
  };
  const removeRow = (uiId: string) => {
    setRows((prev) => prev.filter((r) => r.uiId !== uiId));
  };
  const addRow = () => {
    setRows((prev) => [...prev, makeDraftRow()]);
  };

  const onSave = async () => {
    const attestations: IaAttestationItem[] = rows
      .filter((r) => r.code.trim() !== "")
      .map((r) => {
        const pts = Number(r.points);
        return {
          ia_activity_code: r.code.trim(),
          attested_points: Number.isFinite(pts) ? pts : 0,
        };
      });
    await put.mutateAsync({
      performance_year: year,
      attestations,
    });
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ia-edit-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="border-b border-border px-5 py-3">
          <h3 id="ia-edit-title" className="text-base font-semibold">
            Improvement Activities — {year}
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            {readQ.isLoading
              ? "Loading current attestations…"
              : "Edit or add activities. Save replaces the full IA list — anything not in this list is removed."}
          </p>
        </div>
        <div className="space-y-2 px-5 py-4">
          {rows.map((row, idx) => (
            <div key={row.uiId} className="flex items-center gap-2">
              <div className="w-8 text-xs font-medium text-gray-400">{idx + 1}.</div>
              <input
                type="text"
                placeholder="Activity code (e.g. IA_BE_5)"
                value={row.code}
                onChange={(e) => updateRow(row.uiId, { code: e.target.value })}
                className="flex-1 rounded border border-border px-2 py-1 text-sm tnum"
              />
              <input
                type="number"
                min={0}
                step={5}
                value={row.points}
                onChange={(e) => updateRow(row.uiId, { points: e.target.value })}
                className="w-20 rounded border border-border px-2 py-1 text-sm tnum"
                aria-label="Points"
              />
              <button
                type="button"
                onClick={() => removeRow(row.uiId)}
                aria-label="Remove row"
                className="rounded px-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            className="text-sm text-tw-teal-700 hover:underline"
          >
            + Add activity
          </button>
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={put.isPending}
            className="rounded-button border border-border bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={put.isPending}
            className="rounded-button bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-60"
          >
            {put.isPending ? "Saving…" : "Save attestations"}
          </button>
        </div>
      </div>
    </div>
  );
}
